import type { SimulationActivity } from '../../../../types/simulation';
import { expandActivitiesForExport } from '../../../../utils/simulation/expandActivitiesForExport';
import {
  inferActivityBoardingAndAlightingCounts,
  inferActivityPassengerDelta,
  inferActivityStopKind,
} from '../../../../utils/simulation/inferActivityStopKind';
import { resolveActivityStopAddress, resolveLegAddress } from './utils/addressResolution';
import { resolveActivityTripId } from './utils';

const getVehicleKey = (a: SimulationActivity): string =>
  (a.vehicle_id ?? a.vehicle_shift_id ?? a.shift_id ?? 'global').toString();

function extractTimeMs(a: SimulationActivity): number {
  const fields = [
    a.activity_start_time,
    a.activity_start,
    a.min_activity_start_time,
    a.start_time,
    (a as Record<string, unknown>).time,
  ];
  for (const field of fields) {
    if (field && typeof field === 'string') {
      const parsed = Date.parse(field);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return Number.MAX_SAFE_INTEGER;
}

function getActivityTypeLabel(a: SimulationActivity, index: number, total: number): 'PICK' | 'DROP' | 'PICK/DROP' | 'START' | 'END' | 'OTHER' {
  const paxDelta = inferActivityPassengerDelta(a);
  if (paxDelta !== null) {
    if (paxDelta > 0) return 'PICK';
    if (paxDelta < 0) return 'DROP';
    const kind = inferActivityStopKind(a);
    if (kind === 'pick' || kind === 'drop') return 'PICK/DROP';
  }
  const kind = inferActivityStopKind(a);
  if (kind === 'pick') return 'PICK';
  if (kind === 'drop') return 'DROP';
  if (kind === 'other') {
    if (index === 0) return 'START';
    if (index === total - 1) return 'END';
    return 'OTHER';
  }
  return 'OTHER';
}

function getPaxDelta(activityType: 'PICK' | 'DROP' | 'PICK/DROP' | 'START' | 'END' | 'OTHER', a: SimulationActivity): number {
  const inferred = inferActivityPassengerDelta(a);
  if (inferred !== null) return inferred;
  if (activityType === 'PICK') {
    const n = (a as Record<string, unknown>).passengers_count ?? (a as Record<string, unknown>).number_of_passengers;
    return typeof n === 'number' && n > 0 ? n : 1;
  }
  if (activityType === 'DROP') return -1;
  return 0;
}

function getStartTime(a: SimulationActivity): string {
  const v =
    a.activity_start_time ??
    a.activity_start ??
    a.min_activity_start_time ??
    a.start_time ??
    (a as Record<string, unknown>).time;
  return v && typeof v === 'string' ? v : '';
}

function getEndTime(a: SimulationActivity): string {
  const v = (a as Record<string, unknown>).activity_end_time ?? (a as Record<string, unknown>).activity_end ?? (a as Record<string, unknown>).end_time;
  return v && typeof v === 'string' ? v : '';
}

function getLat(a: SimulationActivity): string {
  const n = a.activity_latitude ?? a.latitude ?? (a as Record<string, unknown>).lat;
  return typeof n === 'number' ? String(n) : '';
}

function getLng(a: SimulationActivity): string {
  const n = a.activity_longitude ?? a.longitude ?? (a as Record<string, unknown>).lng;
  return typeof n === 'number' ? String(n) : '';
}

function getDistance(a: SimulationActivity): string {
  const n = (a as Record<string, unknown>).distance ?? (a as Record<string, unknown>).distance_km ?? (a as Record<string, unknown>).distance_m;
  if (typeof n === 'number') return String(n);
  if (typeof n === 'string') return n;
  return '';
}

function getRideId(a: SimulationActivity): string {
  return resolveActivityTripId(a as Record<string, unknown>);
}

function getActivityCoordinates(
  activity: SimulationActivity
): { lat: number; lng: number } | null {
  const lat =
    activity.activity_latitude ??
    activity.latitude ??
    (activity as Record<string, unknown>).lat;
  const lng =
    activity.activity_longitude ??
    activity.longitude ??
    (activity as Record<string, unknown>).lng;
  if (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  ) {
    return { lat, lng };
  }
  return null;
}

function haversineMeters(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function isOperationalActivityType(type: string): boolean {
  return type === 'START' || type === 'END' || type === 'OTHER';
}

function isRedundantOperationalDuplicate(
  list: SimulationActivity[],
  index: number
): boolean {
  const currType = getActivityTypeLabel(list[index], index, list.length);
  if (currType !== 'OTHER') return false;

  const currTimeMs = extractTimeMs(list[index]);
  const currCoords = getActivityCoordinates(list[index]);
  if (!Number.isFinite(currTimeMs) || !currCoords) return false;

  const nextIndex = index + 1;
  if (nextIndex < list.length) {
    const nextType = getActivityTypeLabel(list[nextIndex], nextIndex, list.length);
    if (nextType === 'END') {
      const nextTimeMs = extractTimeMs(list[nextIndex]);
      const nextCoords = getActivityCoordinates(list[nextIndex]);
      if (
        Number.isFinite(nextTimeMs) &&
        nextCoords &&
        Math.abs(currTimeMs - nextTimeMs) <= 90_000 &&
        haversineMeters(currCoords.lat, currCoords.lng, nextCoords.lat, nextCoords.lng) <= 150
      ) {
        return true;
      }
    }
  }

  const prevIndex = index - 1;
  if (prevIndex < 0) return false;
  const prevType = getActivityTypeLabel(list[prevIndex], prevIndex, list.length);
  if (!isOperationalActivityType(prevType)) return false;

  const prevTimeMs = extractTimeMs(list[prevIndex]);
  const prevCoords = getActivityCoordinates(list[prevIndex]);
  if (!Number.isFinite(prevTimeMs) || !prevCoords) return false;
  if (Math.abs(currTimeMs - prevTimeMs) > 90_000) return false;

  return haversineMeters(currCoords.lat, currCoords.lng, prevCoords.lat, prevCoords.lng) <= 150;
}

function removeRedundantOperationalDuplicates(
  list: SimulationActivity[]
): SimulationActivity[] {
  return list.filter((_activity, index, arr) => !isRedundantOperationalDuplicate(arr, index));
}

function escapeCsvCell(value: string): string {
  if (value === '') return '';
  const needsQuotes = /[",\n\r]/.test(value);
  if (!needsQuotes) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

export interface ActivityCsvRow {
  tripId: string;
  activityStart: string;
  activityType: string;
  activityLatitude: string;
  activityLongitude: string;
  distance: string;
  vehicleId: string;
  activityEndTime: string;
  stopAddress: string;
  pickupAddress: string;
  dropoffAddress: string;
  paxBoarding: string;
  paxAlighting: string;
  paxDelta: string;
  paxOnBoard: string;
}

/**
 * Process activities for CSV: group by vehicle_id, sort by activity_start_time,
 * compute pax_on_board per vehicle, return flat rows for CSV.
 */
export function processActivitiesForCsv(activities: SimulationActivity[]): ActivityCsvRow[] {
  if (!activities || activities.length === 0) return [];

  const expanded = expandActivitiesForExport(activities);

  const byVehicle = new Map<string, SimulationActivity[]>();
  expanded.forEach((a) => {
    const key = getVehicleKey(a);
    if (!byVehicle.has(key)) byVehicle.set(key, []);
    byVehicle.get(key)!.push(a);
  });

  const rows: ActivityCsvRow[] = [];

  byVehicle.forEach((list, vehicleId) => {
    const sorted = [...list].sort((a, b) => extractTimeMs(a) - extractTimeMs(b));
    const cleaned = removeRedundantOperationalDuplicates(sorted);
    let paxOnBoard = 0;

    cleaned.forEach((a, index) => {
      const activityType = getActivityTypeLabel(a, index, cleaned.length);
      const paxDelta = getPaxDelta(activityType, a);
      const boardingAlighting = inferActivityBoardingAndAlightingCounts(a);
      const paxBoarding = boardingAlighting?.boarding ?? 0;
      const paxAlighting = boardingAlighting?.alighting ?? 0;
      paxOnBoard += paxDelta;

      rows.push({
        tripId: getRideId(a),
        activityStart: getStartTime(a),
        activityType,
        activityLatitude: getLat(a),
        activityLongitude: getLng(a),
        distance: getDistance(a),
        vehicleId,
        activityEndTime: getEndTime(a),
        stopAddress: resolveActivityStopAddress(a) ?? '',
        pickupAddress:
          resolveLegAddress({ leg: 'pickup', activity: a, fallbackToStopAddress: true, fallbackToCoords: true }) ?? '',
        dropoffAddress:
          resolveLegAddress({ leg: 'dropoff', activity: a, fallbackToStopAddress: true, fallbackToCoords: true }) ?? '',
        paxBoarding: String(paxBoarding),
        paxAlighting: String(paxAlighting),
        paxDelta:
          activityType === 'PICK' || activityType === 'DROP' || activityType === 'PICK/DROP'
            ? String(paxDelta)
            : '',
        paxOnBoard: String(paxOnBoard),
      });
    });
  });

  return rows;
}

const CSV_HEADERS = [
  'Trip ID',
  'Activity Start',
  'Activity Type',
  'Activity Latitude',
  'Activity Longitude',
  'Distance',
  'Vehicle ID',
  'Activity End Time',
  'Stop Address',
  'Pickup Address',
  'Dropoff Address',
  'Pax Boarding',
  'Pax Alighting',
  'Pax Delta',
  'Pax on Board',
];

function rowToCsvLine(row: ActivityCsvRow): string {
  return [
    escapeCsvCell(row.tripId),
    escapeCsvCell(row.activityStart),
    escapeCsvCell(row.activityType),
    escapeCsvCell(row.activityLatitude),
    escapeCsvCell(row.activityLongitude),
    escapeCsvCell(row.distance),
    escapeCsvCell(row.vehicleId),
    escapeCsvCell(row.activityEndTime),
    escapeCsvCell(row.stopAddress),
    escapeCsvCell(row.pickupAddress),
    escapeCsvCell(row.dropoffAddress),
    escapeCsvCell(row.paxBoarding),
    escapeCsvCell(row.paxAlighting),
    escapeCsvCell(row.paxDelta),
    escapeCsvCell(row.paxOnBoard),
  ].join(',');
}

/**
 * Build CSV string from activities (grouped by vehicle, sorted by time, with pax on board).
 */
export function buildActivitiesCsv(activities: SimulationActivity[]): string {
  const rows = processActivitiesForCsv(activities);
  const headerLine = CSV_HEADERS.join(',');
  const dataLines = rows.map(rowToCsvLine);
  return [headerLine, ...dataLines].join('\r\n');
}

/**
 * Trigger browser download of activities as CSV.
 * Filename: simulation_activities_[runId].csv (or simulation_activities.csv if no runId).
 */
export function downloadActivitiesCSV(activities: SimulationActivity[], runId?: string | null): void {
  const csv = buildActivitiesCsv(activities);
  const filename = runId ? `simulation_activities_${runId}.csv` : 'simulation_activities.csv';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
